/**
 * EmptyState — centered placeholder for empty lists / no-data and error views
 * (Claude Design `.empty`). The icon sits in a 52px framed tile; the `error`
 * tone swaps that frame to the danger palette (`.empty--error`).
 */
import type { ReactElement, ReactNode } from 'react';

import { cn } from './cn.js';

export interface EmptyStateProps {
  title: string;
  description?: ReactNode;
  /** Optional leading icon/illustration, framed in a 52px tile. */
  icon?: ReactNode;
  /** Optional action (e.g. a Button) rendered below the text. */
  action?: ReactNode;
  /** `error` swaps the icon frame to the danger palette. */
  tone?: 'default' | 'error';
  className?: string;
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  tone = 'default',
  className,
}: EmptyStateProps): ReactElement {
  return (
    <div
      className={cn(
        'flex h-full flex-col items-center justify-center gap-1.5 px-6 py-[60px] text-center',
        className,
      )}
    >
      {icon ? (
        <div
          className={cn(
            'mb-2 grid h-[52px] w-[52px] place-items-center rounded-[15px] [&_svg]:size-6',
            tone === 'error'
              ? 'border border-transparent bg-danger-soft text-danger'
              : 'border border-border bg-surface-2 text-faint',
          )}
        >
          {icon}
        </div>
      ) : null}
      <p className="text-base font-bold text-text">{title}</p>
      {description ? (
        <p className="max-w-[320px] text-[13px] leading-relaxed text-muted">
          {description}
        </p>
      ) : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}
